import type { IconName } from "@/components/ui/Icon";

/* Every destination the UI can link to lives here, so a link and the page
   behind it can never drift apart. */

/* The sportsbook's root. The BetBy renderer owns every route beneath it and
   rewrites the URL as the visitor moves through the book, so the app treats
   the whole subtree as this one destination. */
export const SPORTSBOOK_ROOT = "/sports";

/* The kit's two desktop shells: a persistent left rail, or navigation folded
   into the top of the centre column instead. Mobile is the same tab bar
   either way — the kit has no separate mobile Tile frame. "Rail" is a third:
   a narrow rail that never collapses, every item an icon over its label
   (Hugo Casino's). "Combined" is the Topbar layout with the whole side menu
   a menu button away: a drawer from the left, on a desktop as on a phone
   (LeoVegas's, and Vega's). "Combined rail" is Combined with the menu's
   icons kept on screen in a narrow rail under the bar on desktop, the menu
   button widening that rail over the page to show their labels (Capella's,
   after Betmode); a phone keeps the drawer. */
export const NAV_LAYOUTS = ["Sidebar", "Tile", "Centered", "Tabs", "Rail", "Topbar", "Combined", "Combined rail"];

export const usesLabelledRail = (layout: string) => layout === "Rail";

/* "Topbar": no side menu at all — the menu's links sit in the top bar
   beside the logo, the bar spans the whole width, and the chat (when on
   the left) sits under it (Nebula's layout, after duel.com). */
export const usesTopbarNav = (layout: string) => layout === "Topbar" || layout === "Combined" || layout === "Combined rail";

/* Combined: the Topbar's links, and a menu button opening the side menu as a
   drawer, desktop included. */
export const usesMenuDrawer = (layout: string) => layout === "Combined" || layout === "Combined rail";

/* Combined rail: the menu's icons in a rail under the bar, desktop only. */
export const usesBarRail = (layout: string) => layout === "Combined rail";

/* A Topbar menu link's drop-down: none, the platform's casino categories,
   or the games of the category the link opens (`/c/<slug>`). */
export const NAV_MENUS = ["None", "Casino categories", "Games"];

/* Which side of the page the chat rail sits on. */
export const CHAT_SIDES = ["Right", "Left"];

/* Sign in beside Register: an outline (Hugo), a filled button in the
   raised surface colour (Nebula), or plain text (Altair). */
export const SIGN_IN_STYLES = ["Outline", "Filled", "Text"];

/* The top bar: a solid surface; glass over the page — translucent and
   blurred, its menu links as soft pills; or floating — the same glass as a
   rounded capsule held off the top and sides, as wide as the page column
   (mint.io's, and Rigel's). "Brand": solid in the brand colour, its text
   and buttons re-toned to read on it (LeoVegas's orange bar, Vega's). */
export const HEADER_STYLES = ["Solid", "Glass", "Floating", "Brand"];

export const isGlassHeader = (style: string) => style === "Glass" || style === "Floating";

/* Where the top bar keeps its ways in: at the right with everything else,
   or gathered in the middle of the bar — rewards, Sign in and Register (or
   the wallet) and notifications — over a soft glow (thrill.com's, and
   Altair's). */
export const HEADER_ACTIONS = ["Right", "Center"];

/* How wide the site runs on a large screen: edge to edge, or boxed in a
   centred column (at most 1440px) with the canvas or backdrop around it —
   the top bar's and footer's content lined up with the page's (mint.io's). */
export const PAGE_WIDTHS = ["Full", "Centered"];

/* How the category row draws, everywhere it appears — the lobby, the
   category page and the homepage's game browser alike: "Pills" are the
   kit's nav-tab-pill; "Tabs" a row of icons over their labels, underlined
   when lit. */
export const CATEGORY_STYLES = ["Pills", "Tabs"];

/* How the top bar draws the logo. "Icon": a small square mark, with the site
   name beside it when `wordmark` is on. "Wide image": the uploaded image
   alone, sized for a wide logo that already carries the name (NitroBet's). */
export const LOGO_STYLES = ["Icon", "Wide image"];

/* "Centered" is the Tile layout with its tab row, category pills and the logo
   on the page's centre line (NitroBet's layout). Both draw the tile rows
   instead of the left rail, so anything that asks "is there a rail?" asks
   this rather than comparing to "Tile". */
export const usesTileNav = (layout: string) => layout === "Tile" || layout === "Centered" || layout === "Tabs";

/* "Tabs": the Tile frame with ONE row under the top bar, the menu's own
   links (nav.primary, then nav.tail) as tabs — no product row and no
   platform categories (Topzinho's, Aldebaran's). */
export const isSingleRowNav = (layout: string) => layout === "Tabs";
export const isCenteredNav = (layout: string) => layout === "Centered";

/* How the sidebar draws its groups, the kit's two sidebar variations:
   "Grouped" is casino / grouped — each group a collapsible card with its icon
   and a chevron. "Non-grouped" is casino / non-grouped — every group laid out
   open under a small uppercase label, divided like the primary links. */
export const NAV_GROUP_STYLES = ["Grouped", "Non-grouped"];

export const FOOTER_COLUMNS: { title: string; links: { label: string; href: string; external?: boolean }[] }[] = [
  {
    title: "Casino",
    links: [
      { label: "Casino Games", href: "/c/all" },
      { label: "Slots", href: "/c/slots" },
      { label: "Live Casino", href: "/c/live-casino" },
      { label: "Crash Games", href: "/c/crash-games" },
      { label: "Game Shows", href: "/c/game-show" },
      { label: "Studios", href: "/studios" },
      { label: "Leaderboards", href: "/leaderboards" },
    ],
  },
  {
    /* One link, because the sportsbook owns every route below it. BetBy's own
       navigation decides what the sports are, so listing them here would be
       inventing a menu the renderer does not have to honour. */
    title: "Sports",
    links: [{ label: "Sportsbook", href: SPORTSBOOK_ROOT }],
  },
  {
    title: "Support",
    links: [
      { label: "Help Center", href: "/support/help-center" },
      { label: "Fairness", href: "/support/fairness" },
      { label: "Responsible Gaming", href: "/support/responsible-gaming" },
      { label: "Gambling Helpline", href: "https://www.begambleaware.org", external: true },
      { label: "Live Support", href: "/support/live-support" },
      { label: "Self Exclusion", href: "/support/self-exclusion" },
    ],
  },
  {
    title: "About Us",
    links: [
      { label: "Affiliate", href: "/about/affiliate" },
      { label: "Privacy Policy", href: "/legal/privacy-policy" },
      { label: "AML Policy", href: "/legal/aml-policy" },
      { label: "Terms of Service", href: "/legal/terms-of-service" },
    ],
  },
  {
    title: "FAQ",
    links: [
      { label: "How-to Guide", href: "/blog/how-to-guide" },
      { label: "Online Casino Guide", href: "/blog/online-casino-guide" },
      { label: "Sports Betting Guide", href: "/blog/sports-betting-guide" },
      { label: "All Guides", href: "/blog" },
    ],
  },
];

export const SOCIAL_LINKS: { icon: IconName; label: string; href: string }[] = [
  { icon: "telegram", label: "Telegram", href: "https://t.me" },
  { icon: "discord", label: "Discord", href: "https://discord.com" },
  { icon: "x", label: "X", href: "https://x.com" },
  { icon: "instagram", label: "Instagram", href: "https://instagram.com" },
  { icon: "facebook", label: "Facebook", href: "https://facebook.com" },
  { icon: "youtube", label: "YouTube", href: "https://youtube.com" },
];

/* Long-form pages served by the /legal, /support and /about routes. */
export type Doc = { title: string; intro: string; sections: { h: string; p: string }[] };

export const LEGAL_DOCS: Record<string, Doc> = {
  "privacy-policy": {
    title: "Privacy Policy",
    intro:
      "This policy explains what Flipcoin.com collects, why we collect it, and what you can ask us to delete. It applies to the website, the mobile web app and every account action taken through them.",
    sections: [
      { h: "What we collect", p: "Account details you give us at sign-up, verification documents you upload, wallet addresses you deposit from, and technical data your browser sends on each request: IP address, user agent, and the pages you open." },
      { h: "Why we collect it", p: "To run your account, to meet the anti-money-laundering obligations of our licence, to detect bonus abuse and multi-accounting, and to keep the product working. We do not sell personal data to anybody, ever." },
      { h: "How long we keep it", p: "Verification records are retained for five years after account closure because our licence requires it. Everything else is deleted within 90 days of you closing the account and asking us to erase it." },
      { h: "Your rights", p: "You can correct anything wrong through Settings → Account → Edit details. This site does not yet have a way to export what we hold on you or to process an erasure request." },
    ],
  },
  "aml-policy": {
    title: "AML Policy",
    intro:
      "Our anti-money-laundering programme sets out how we identify players, monitor activity, and report anything suspicious to the regulator that licenses us.",
    sections: [
      { h: "Customer due diligence", p: "Every account is screened at registration. Enhanced due diligence is triggered by cumulative deposits, unusual funding patterns, or a match against sanctions and politically-exposed-person lists." },
      { h: "Ongoing monitoring", p: "Deposits, wagers and withdrawals are scored continuously. Patterns such as depositing and immediately withdrawing without meaningful play are flagged for manual review." },
      { h: "Source of funds", p: "Above defined thresholds we will ask for evidence of where the money came from. Play continues while a review is open; withdrawals are held until it closes." },
      { h: "Reporting", p: "Suspicious activity reports go to the regulator that licenses us. We are legally prohibited from telling you that a report has been made." },
    ],
  },
  "terms-of-service": {
    title: "Terms of Service",
    intro:
      "These terms form the contract between you and the operator of Flipcoin.com. Opening an account means you accept them.",
    sections: [
      { h: "Eligibility", p: "You must be at least 18 years old and resident in a jurisdiction where online gambling is lawful. One account per person, household and device." },
      { h: "Deposits and withdrawals", p: "Funds are held in a segregated wallet. Withdrawals return to the method used to deposit wherever the payment rails allow it. We never charge a withdrawal fee." },
      { h: "Bonuses", p: "Cashback and rakeback pay into your withdrawable balance with no rollover. Deposit-match bonuses carry the wagering requirement stated on the offer at the time you claim it, and never more." },
      { h: "Fair play", p: "Bots, arbitrage across linked accounts, and exploiting a demonstrable software fault are grounds for closing an account and voiding the winnings from the affected rounds." },
      { h: "Disputes", p: "Contact support first. If we cannot resolve it within 14 days you may escalate to our licensing authority, whose decision binds both parties." },
    ],
  },
};

export const SUPPORT_DOCS: Record<string, Doc> = {
  "help-center": {
    title: "Help Center",
    intro: "The fastest answers to the questions support gets most. If none of these fit, live chat is staffed by people, 24 hours a day.",
    sections: [
      { h: "My withdrawal is still pending", p: "Withdrawals under the hot-wallet threshold settle in under a minute. Anything larger moves to a co-signed cold-wallet flow, which is reviewed by two people and typically clears within two hours." },
      { h: "I can't complete verification", p: "The most common cause is a document photo where a corner is cropped out. Photograph the whole document flat, on a dark surface, with no flash." },
      { h: "A game disconnected mid-round", p: "Rounds are settled server-side. Reopen the game and the result will be applied to your balance; nothing is lost when a connection drops." },
      { h: "I want to close my account", p: "This site does not have an account-closure control yet. There is no tab in Settings that closes an account or pays out a final balance." },
    ],
  },
  fairness: {
    title: "Fairness",
    intro: "Every round we settle writes a transaction hash to the live feed, and support can pull the full record for it. This page explains how, and what the numbers in it mean.",
    sections: [
      { h: "Provably fair rounds", p: "Each round combines a server seed we commit to in advance with a client seed you control. After the round the server seed is revealed, so you can recompute the outcome and confirm nothing changed mid-round." },
      { h: "Verifying a payout", p: "Every bet in the live feed shows the hash of the round that settled it, next to the payout. It is text rather than a link — copy it, send it to support with your username, and an agent can pull the full record for that round." },
      { h: "RTP, honestly", p: "We publish both the studio's original RTP and the RTP configured on our integration. Where they differ, the difference is shown on the game page rather than buried in a help article." },
      { h: "Third-party testing", p: "Original games are audited annually by an independent testing house. Third-party studio titles carry their own certification from the studio." },
    ],
  },
  "responsible-gaming": {
    title: "Responsible Gaming",
    intro: "Gambling should stay entertainment. Here is where things stand today, and how to reach real help if it stops being fun.",
    sections: [
      { h: "Deposit limits", p: "This site does not have deposit limits. There is no cap to set, and nothing here restricts what you can deposit." },
      { h: "Reality checks", p: "This site does not have a session reminder. Nothing here will interrupt play or show your session length or net position — keep track yourself if that matters to you." },
      { h: "Time-out and self-exclusion", p: "This site does not have a time-out or self-exclusion control. There is nothing here that locks your account for a period, short or long." },
      { h: "Getting help", p: "BeGambleAware and GamCare offer free, confidential support independent of any operator. Their helplines are open around the clock." },
    ],
  },
  "live-support": {
    title: "Live Support",
    intro: "Live chat is staffed by people, every hour of every day. No bots, no ticket queue, no scripted deflection.",
    sections: [
      { h: "Opening a chat", p: "The chat panel on the right of the desktop layout is the same channel our agents watch — it needs a wide screen, so it is not there on a phone. Open the panel and say what happened — an agent picks it up, typically within a minute." },
      { h: "What to include", p: "Your username and, if it is about a specific round, the hash shown next to that bet in the live feed. That is usually enough for an agent to resolve it in the first reply." },
      { h: "Escalation", p: "If an agent cannot resolve it, ask for it to be escalated. A shift lead reviews escalations the same day and replies in the same thread." },
    ],
  },
  "self-exclusion": {
    title: "Self Exclusion",
    intro: "Self-exclusion is a tool that locks a player out of a gambling site for a fixed period, deliberately hard to undo once started. This site does not have it yet.",
    sections: [
      { h: "How to start", p: "This site does not have a self-exclusion control. There is no tab in your account that starts one, and nothing here locks it automatically." },
      { h: "What it blocks", p: "Nothing — there is no exclusion for this site to enforce, because the control does not exist here yet." },
      { h: "It cannot be lifted early", p: "There is no active exclusion for support to lift or extend. The control this page describes is not built yet." },
    ],
  },
};

export const ABOUT_DOCS: Record<string, Doc> = {
  affiliate: {
    title: "Affiliate Programme",
    intro: "Send players who stay, get paid a share of what they generate. Reporting is real-time and the terms do not change retroactively.",
    sections: [
      { h: "Commission", p: "Revenue share starts at 25% and rises to 45% by volume, calculated on net gaming revenue with no negative carry-over between months." },
      { h: "Reporting", p: "Clicks, registrations, first deposits and revenue are visible in the dashboard as they happen, not on a 24-hour delay." },
      { h: "Payments", p: "Paid on the first business day of each month in USDC, BTC or by bank transfer. There is no minimum threshold." },
      { h: "What we won't do", p: "We do not shave commissions, we do not reassign your players to house accounts, and we do not change your rate without telling you first." },
    ],
  },
};

export const BLOG_POSTS: Record<string, Doc> = {
  "why-flipcoin": {
    title: "Why we built Flipcoin",
    intro:
      "We had been gambling online for years and got tired of what everyone else did. This is what we changed, and how you can check that we actually did it.",
    sections: [
      { h: "How on-chain payouts actually work", p: "Every settled bet writes a transaction hash. It is text rather than a link — copy it and send it to support with your username, and an agent can pull the full record. There is no internal ledger you have to trust: the payout, the multiplier and the timestamp are public the moment the round closes." },
      { h: "Why we removed wagering requirements", p: "A bonus with a 40x rollover is not a bonus, it is a lock. We pay cashback and rakeback straight into the withdrawable balance instead. It costs us more per player and it is the single most common reason people tell us they stayed." },
      { h: "Withdrawals in under a minute", p: "Hot wallet balances cover the vast majority of cashouts, so the request is signed and broadcast without a human in the loop. Anything above that threshold moves to a co-signed cold wallet flow, which is the only case where you will wait." },
    ],
  },
  "how-to-guide": {
    title: "How-to Guide",
    intro: "Everything from opening an account to cashing out your first win, in the order you will actually need it.",
    sections: [
      { h: "1. Create an account", p: "An email address and a password. Verification is only required before your first withdrawal, so you can deposit and play immediately." },
      { h: "2. Fund it", p: "Open the wallet, pick a currency and a network, and send to the address shown. Crypto deposits credit after one confirmation; card and bank deposits credit instantly." },
      { h: "3. Play", p: "Use the filter pills and search to narrow the lobby. Every game page shows the studio, the original RTP and the RTP we run it at." },
      { h: "4. Withdraw", p: "Wallet → Withdraw. Complete verification once, then withdrawals settle in under a minute up to the hot-wallet threshold." },
    ],
  },
  "online-casino-guide": {
    title: "Online Casino Guide",
    intro: "What the numbers on a slot actually mean, and which of them are worth caring about.",
    sections: [
      { h: "RTP", p: "Return to player is the long-run average returned across millions of rounds. It tells you nothing about your session and everything about which games to prefer over a year." },
      { h: "Volatility", p: "High volatility means rarer, larger wins and a wider swing in bankroll. It is the single biggest driver of how a game feels to play." },
      { h: "Max win", p: "The cap on a single round, expressed as a multiple of your bet. A 25,000x cap is meaningless if the hit frequency for it is one in fifty million." },
      { h: "Crypto and fiat", p: "Balances are held in the currency you deposited. Switching your display currency converts at the live rate and never touches the underlying balance." },
    ],
  },
  "sports-betting-guide": {
    title: "Sports Betting Guide",
    intro: "Odds formats, margins, and how to read a price before you take it.",
    sections: [
      { h: "Reading the price", p: "Decimal odds are the total return per unit staked, stake included. 2.50 returns 2.50 for every 1.00 risked, so 1.50 of profit." },
      { h: "Margin", p: "Convert every price in a market to an implied probability and add them up. Anything over 100% is the book's margin. Ours sits around 3% on major markets." },
      { h: "Live betting", p: "Prices move with the run of play and are suspended around goals and reviews. A bet placed during suspension is rejected rather than settled at a stale price." },
      { h: "Acca insurance", p: "Build a five-leg accumulator. If exactly one leg loses, the stake comes back as a free bet up to $50." },
    ],
  },
};
